/**
 * Création d'une préévaluation Manager — US-10 / Lot 8.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  domainsApi,
  preevalApi,
  ApiRequestError,
  type DomainDto,
  type ApplicationDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { useAuth } from "../context/AuthContext";
import { localeLabel } from "../utils/locale";

type ExpertOpt = { id: string; fullName: string; internalId: string };

export function PreevalEntryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.accessLevel ?? 0) >= 100;

  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [erp, setErp] = useState<ApplicationDto[]>([]);
  const [experts, setExperts] = useState<ExpertOpt[]>([]);
  const [domain, setDomain] = useState("");
  const [applicationId, setErpId] = useState("");
  const [expertUserId, setExpertUserId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, e] = await Promise.all([domainsApi.list(), assessmentApi.listErp()]);
        if (cancelled) return;
        let list = d.domains.filter((x) => x.active);
        if (!isAdmin && user?.domains?.length) {
          const allowed = new Set(user.domains.map((c) => c.toUpperCase()));
          list = list.filter((x) => allowed.has(x.code.toUpperCase()));
        }
        setDomains(list);
        setErp(e.applications);
        if (list[0]) setDomain(list[0].code);
        // application is optional — default to empty
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.domains]);

  useEffect(() => {
    if (!domain) {
      setExperts([]);
      setExpertUserId("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await preevalApi.listExperts(domain);
        if (cancelled) return;
        setExperts(res.experts);
        setExpertUserId(res.experts[0]?.id ?? "");
      } catch (err) {
        if (!cancelled) {
          setExperts([]);
          setError(err instanceof ApiRequestError ? err.message : t("experts_not_found"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!domain || !expertUserId) {
      setError(t("required_expert_domain"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { preeval } = await preevalApi.create({ expertUserId, domain, applicationId: applicationId || undefined, year });
      navigate(`/preevals/${preeval.id}`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <PageHeader
        title={t("new_preeval")}
        subtitle={t("new_preeval_sub")}
      />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="sm-form-card" style={{ maxWidth: 520 }}>
        <label className="sm-muted">{t("domain")}</label>
        <select
          className="sm-input"
          required
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          style={{ marginBottom: 12, width: "100%" }}
        >
          <option value="">—</option>
          {domains.map((d) => (
            <option key={d.id} value={d.code}>
              {d.code} — {localeLabel(i18n.language, d)}
            </option>
          ))}
        </select>

        <label className="sm-muted">{t("preeval_expert_short")}</label>
        <select
          className="sm-input"
          required
          value={expertUserId}
          onChange={(e) => setExpertUserId(e.target.value)}
          style={{ marginBottom: 12, width: "100%" }}
        >
          <option value="">—</option>
          {experts.map((ex) => (
            <option key={ex.id} value={ex.id}>
              {ex.fullName} ({ex.internalId})
            </option>
          ))}
        </select>

        <label className="sm-muted">
          {t("application")}
          <span className="sm-muted" style={{ fontWeight: "normal", marginLeft: 6, fontSize: "0.85em" }}>
            ({t("optional")})
          </span>
        </label>
        <select
          className="sm-input"
          value={applicationId}
          onChange={(e) => setErpId(e.target.value)}
          style={{ marginBottom: 12, width: "100%" }}
        >
          <option value="">{t("no_application")}</option>
          {erp.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>

        <label className="sm-muted">{t("year")}</label>
        <input
          type="number"
          className="sm-input"
          required
          min={2020}
          max={2100}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ marginBottom: 16, width: "100%", maxWidth: 160 }}
        />

        <div className="sm-form-actions">
          <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
            {t("create_and_score")}
          </button>
          <button
            type="button"
            className="sm-btn sm-btn-ghost"
            disabled={busy}
            onClick={() => navigate("/preevals")}
          >
            {t("cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
